import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import { rtApiDemoActions, rtApiListActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { Col, Container, Div, Row, Icon, Text } from '@pinpoint/design-kit';
import DefaultTheme from 'src/styles/DefaultTheme';
import { Button, Message } from 'src/components';
import CONSTANTS from 'src/store/constants';

const TAB_KEYS = {
  BODY: 'BODY',
  HEADERS: 'HEADERS',
};
const API_VERSION = '1.1.0';
const RT_API = `https://${process.env.REACT_APP_ENV !== 'prod' ? 'stage-' : ''}api.pinpointpredictive.io/scores`;
const EXAMPLE_API_KEY = '<API_KEY>';
const EXAMPLE_CONFIG_ID = '<CS_ID>';
const EXAMPLE_PREDICTION = {
  [CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY]: {
    type: 'claims_frequency',
    value: 0.0123456789,
  },
  [CONSTANTS.TAL_MODEL.TAG.ACT_SEVERITY]: {
    type: 'claims_severity',
    value: 12345.67,
  },
  [CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_DIRECT]: {
    type: 'loss_cost_direct',
    value: 1234.56,
  },
  [CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_MULT]: {
    type: 'loss_cost_multiplied',
    value: 1234.56,
  },
  [CONSTANTS.TAL_MODEL.TAG.CLASSIFIER]: {
    type: 'binary_classifier',
    value: 123.45,
  },
  test: {
    type: 'test',
    value: 1234567.0123456789,
  },
};

const bodyText =
  '{"first_name": "", "last_name": "", "address": "", "city": "", "state": "XX", "zip": "XXXXX", "email": "", "phone": "XXXXXXXXXX"}';

const responseHeadersText = `access-control-allow-origin: *
content-length: 80
content-type: application/json; charset=utf-8
date: Wed, 26 Apr 2023 15:36:12 GMT
etag: W/"50-SeQat0CcoBHupki/AaCcBDxCTtE"`;

function RtApiDocumentation({
  history,
  app: {
    profile: { tenant },
  },
  match: {
    params: { configId },
  },
  rtApiList: { apiKey, fetchApiKeyStatus },
  rtApiDemo: { config, fetchConfigStatus },
  fetchRealTimeApiKey,
  fetchConfig,
  dispatch,
}) {
  const [selectedTab, setSelectedTab] = useState(TAB_KEYS.BODY);

  const displayCs = useMemo(
    () => ([null, '', undefined].includes(configId) ? EXAMPLE_CONFIG_ID : configId),
    [configId]
  );
  const apiUrl = useMemo(() => `${RT_API}?cs=${displayCs}`, [displayCs]);
  const isActuarialModel = useMemo(
    () =>
      [config?.config_metadata?.data_model_type, config?.config_metadata?.model?.dataModelType].some((type) =>
        (type || '').includes('ACT_')
      ),
    [config]
  );
  const loading = useMemo(
    () => fetchApiKeyStatus === CONSTANTS.API.STARTED || fetchConfigStatus === CONSTANTS.API.STARTED,
    [fetchApiKeyStatus, fetchConfigStatus]
  );

  useEffect(() => {
    if (tenant) {
      fetchRealTimeApiKey();
    }
    if (tenant && ![null, undefined, ''].includes(configId)) {
      fetchConfig(configId);
    }
    return () => dispatch(rtApiDemoActions.unset('config'));
  }, [tenant, fetchRealTimeApiKey, apiKey, configId, fetchConfig, dispatch]);

  const requestText = useCallback(
    (wrapLines) =>
      `curl --request POST \\
--location '${apiUrl}' \\
--header 'Authorization: ApiKey ${fetchApiKeyStatus === CONSTANTS.API.SUCCEEDED ? apiKey : EXAMPLE_API_KEY}' \\
--header 'Content-Type: application/json' \\
--data '${wrapLines ? JSON.stringify(JSON.parse(bodyText), null, 4) : bodyText}'`,
    [apiKey, fetchApiKeyStatus, apiUrl]
  );

  /* define the documentation text for each model type */
  const responseBodyText = useMemo(
    () =>
      `{"api_version": "${API_VERSION}", "cs": "${displayCs}", "match_type": "D", "prediction": {"percentile_persons": 50, ${
        isActuarialModel ? `"percentile_premiums": 50, ` : ''
      }"type": "${EXAMPLE_PREDICTION[config?.config_metadata.data_model_type ?? 'test'].type}", "value": ${
        EXAMPLE_PREDICTION[config?.config_metadata.data_model_type ?? 'test'].value
      }}}`,
    [displayCs, config, isActuarialModel]
  );

  const sampleLossPredictionText = useMemo(
    () =>
      `{"api_version": "${API_VERSION}", "cs": "MRp5QB2D", "match_type": "D", "prediction": {"percentile_persons": 75.5, "percentile_premiums": 77.6, "type": "${
        EXAMPLE_PREDICTION[CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY].type
      }", "value": 0.0635454677}}`,
    []
  );

  const sampleRiskScoreText = useMemo(
    () =>
      `{"api_version": "${API_VERSION}", "cs": "MRp5QB2D", "match_type": "D", "prediction": {"percentile_persons": 42.3, "type": "${
        EXAMPLE_PREDICTION[CONSTANTS.TAL_MODEL.TAG.CLASSIFIER].type
      }", "value": 502.58}}`,
    []
  );

  const sampleTestText = useMemo(
    () =>
      `{"api_version": "${API_VERSION}", "cs": "MRp5QB2D", "match_type": "D", "prediction": {"percentile_persons": 50, "percentile_premiums": 50, "type": "${EXAMPLE_PREDICTION.test.type}", "value": ${EXAMPLE_PREDICTION.test.value}}}`,
    []
  );

  const sampleNonMatchText = useMemo(
    () => `{"api_version": "${API_VERSION}", "cs": "MRp5QB2D", "match_type": "N"}`,
    []
  );

  const invalidCSFailure = useMemo(
    () =>
      `{"error": { "message": "Invalid cs specified, please contact support for a valid customer setting", "error_code": "INV_CS" }}`,
    []
  );

  const serverErrorDuringRequest = useMemo(
    () => `{"error": { "message": "Server error during request", "error_code": "SVC_ERROR" }}`,
    []
  );

  const serviceUnavailable = useMemo(
    () =>
      `{"error": { "message": "OnPoint Service is unavailable; please contact support@pinpoint.ai", "error_code": "SVC_ERROR" }}`,
    []
  );

  const modelLoading = useMemo(
    () => `{"error": { "message": "Model loading; please try again in 20 seconds", "error_code": "SVC_INIT" }}`,
    []
  );

  return (
    <Container w="100%" minW="100%">
      <ReactTooltip id="custom-wrap-lines" className="pinpoint-tooltip" effect="solid" place="bottom" delayShow={500} />
      <ReactTooltip id="custom-copy" className="pinpoint-tooltip" effect="solid" place="bottom" delayShow={500} />
      <Row flex-grow="0" p={{ t: '1.5rem' }} m={{ x: '.5rem' }} data-debug="nav-row">
        <Col size="12" gutterWidth="0">
          <Div d="flex" justify="space-between">
            <Div d="flex" justify="flex-start" align="center">
              <Button
                buttonStyle="primary"
                onClick={() => {
                  history.push('/rt');
                  dispatch(rtApiDemoActions.unset('config'));
                }}
              >
                <Icon color="white" name="LongLeft" m={{ r: '0.5rem' }} size="1rem" />
                BACK
              </Button>

              <Button
                buttonStyle="primary"
                onClick={() => {
                  window.open('/static/Pinpoint API Documentation_API v1.1.1_12-VI-24.pdf');
                }}
                data-tip="Download real-time API documentation"
              >
                <Icon name="Download2" color="white" size="1rem" viewBox="0 0 14 14" m={{ r: '.5rem' }} />
                DOWNLOAD
              </Button>
            </Div>

            <Div d="flex" justify="flex-end" align="center">
              <Text textColor="navyBlue" textSize="title">
                OnPoint Real-Time API
              </Text>
            </Div>
          </Div>
        </Col>
      </Row>

      {loading && <Message />}

      {((!loading && configId && config) || (!loading && !configId)) && (
        <>
          <Row data-debug="layout-row" m={{ x: '0.5rem' }}>
            <Col
              data-debug="info-column"
              size={{ xs: '12', sm: '12', md: '12', lg: 'auto', xl: 'auto' }}
              gutterWidth="0"
            >
              <Div
                rounded="sm"
                p={{ x: '1.25rem', y: '1.25rem' }}
                m={{ y: '1rem' }}
                bg="white"
                d="flex"
                flexDir="column"
                minH="615px"
              >
                <Row data-debug="api-title" m={{ x: '0' }} flexDir="column" flexGrow="0">
                  <Text tag="h1">
                    <Text tag="span" textColor="marigold" style={{ fontSize: '18px' }}>
                      Post&nbsp;&nbsp;
                    </Text>
                    <Text tag="span" textColor="navyBlue" style={{ fontSize: '18px' }}>
                      -&nbsp;&nbsp;Find predictions for a user
                    </Text>
                  </Text>

                  <Div
                    m={{ x: '0', y: '1rem' }}
                    p={{ x: '.8rem', y: '.35rem' }}
                    bg="shadeF2"
                    border="1px solid"
                    borderColor="shadeCC"
                    rounded="sm"
                    textColor="shade66"
                    textSize="caption"
                    style={{ wordBreak: 'break-all' }}
                  >
                    {apiUrl}
                  </Div>
                </Row>

                <Row data-debug="headers" m={{ x: '0' }} flexDir="column" flexGrow="0" flexWrap="">
                  <Text tag="h2" textColor="shade33" textSize="input">
                    Headers
                  </Text>
                  <Div
                    m={{ x: '0', y: '.5rem' }}
                    p={{ x: '1rem', y: '1rem' }}
                    bg="white"
                    border="1px solid"
                    borderColor="shadeCC"
                    rounded="sm"
                    textColor="shade99"
                    overflow="auto"
                    textSize="body"
                  >
                    <Row>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }} textWeight="600">
                        <Text>Authorization</Text>
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }} h="0">
                        <Text style={{ wordBreak: 'break-all' }} p={{ b: '1rem' }}>
                          ApiKey {fetchApiKeyStatus === CONSTANTS.API.SUCCEEDED ? apiKey : EXAMPLE_API_KEY}
                        </Text>
                      </Col>
                    </Row>
                  </Div>
                </Row>

                <Row data-debug="params" m={{ x: '0' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h2" textColor="shade33" textSize="input">
                    Params
                  </Text>
                  <Div
                    m={{ x: '0', y: '.5rem' }}
                    p={{ x: '1rem', y: '1rem' }}
                    bg="white"
                    border="1px solid"
                    borderColor="shadeCC"
                    rounded="sm"
                    textColor="shade99"
                    flexGrow="1"
                    minH="120px"
                    overflow="auto"
                    textSize="body"
                  >
                    <Row>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }} textWeight="600">
                        <Text>cs</Text>
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }} h="0">
                        <Text>{displayCs}</Text>
                        <Text m={{ t: '0.75rem' }} p={{ b: '1rem' }}>
                          Config Setting ID (8 character id for API config)
                        </Text>
                      </Col>
                    </Row>
                  </Div>
                </Row>

                <Row data-debug="body" m={{ x: '0' }} flexDir="column" flexGrow="2" flexWrap="">
                  <Text tag="h2">
                    <Text tag="span" textColor="shade33" textSize="input">
                      Body
                    </Text>
                    <Text tag="span" textColor="shadeAF" textSize="caption" m={{ l: '.65rem' }}>
                      raw (json)
                    </Text>
                  </Text>

                  <Div
                    m={{ x: '0', t: '.5rem' }}
                    p={{ x: '0', y: '0' }}
                    bg="white"
                    border="1px solid"
                    borderColor="shadeCC"
                    rounded="sm"
                    textColor="shade99"
                    flexGrow="1"
                    minH="120px"
                    textSize="body"
                  >
                    <Row>
                      <Col size={{ xs: '12', sm: '12', md: '6', lg: '12', xl: '6' }}>
                        <CodeBlock
                          blockType="json"
                          theme="light"
                          divProps={{ minH: '245px', m: { t: '0' }, border: '' }}
                        >
                          {bodyText}
                        </CodeBlock>
                      </Col>
                      <Col
                        size={{ xs: '12', sm: '12', md: '6', lg: '12', xl: '6' }}
                        style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}
                      >
                        <Text m={{ x: '0', t: '1rem', b: '1.5rem' }}>
                          If any fields are not available, leave blank.
                        </Text>
                        <Row m={{ l: '-0.5rem', y: '1rem' }}>
                          <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                            <Div tag="span" textWeight="600">
                              state
                            </Div>
                          </Col>
                          <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                            <Text>Capitalized two-letter abbreviation</Text>
                          </Col>
                        </Row>

                        <Row m={{ b: '1rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                        <Row m={{ l: '-0.5rem', y: '1rem' }}>
                          <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                            <Div tag="span" textWeight="600">
                              zip
                            </Div>
                          </Col>
                          <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                            <Text>5 digits (including a leading 0 where relevant)</Text>
                          </Col>
                        </Row>

                        <Row m={{ b: '1rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                        <Row m={{ l: '-0.5rem', y: '1rem' }}>
                          <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                            <Div tag="span" textWeight="600">
                              phone
                            </Div>
                          </Col>
                          <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                            <Text>10 digits with no special characters</Text>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Div>
                </Row>
              </Div>
            </Col>

            {/* Spacer column for responsive layout */}
            <Col />

            <Col
              data-debug="examples-column"
              size={{ xs: '12', sm: '12', md: '12', lg: 'auto', xl: 'auto' }}
              gutterWidth="0"
              d="flex"
            >
              <Div
                rounded="sm"
                p={{ x: '1.25rem', y: '1.25rem' }}
                m={{ y: '1rem' }}
                bg="shade33"
                d="flex"
                flexDir="column"
                flexGrow="1"
                minH="615px"
              >
                <Row data-debug="example-request" m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h2" textColor="white" textSize="input">
                    Example Request
                  </Text>

                  <CodeBlock blockType="curl" theme="dark" divProps={{ minH: '230px' }}>
                    {requestText}
                  </CodeBlock>
                </Row>

                <Row data-debug="example-response" m={{ x: '0', t: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h2" textColor="white" textSize="input">
                    Example Response
                  </Text>

                  <Row m={{ t: '.6rem' }}>
                    <Button
                      style={{
                        boxShadow: selectedTab === TAB_KEYS.BODY ? `0 0 0 1px ${DefaultTheme.colors.marigold}` : '',
                      }}
                      bg="transparent"
                      p={{ x: '.85rem', y: '.55rem' }}
                      m={{ r: '.25rem' }}
                      h="0"
                      textSize="caption"
                      rounded="xs"
                      onClick={() => setSelectedTab(TAB_KEYS.BODY)}
                    >
                      Body
                    </Button>
                    <Button
                      style={{
                        boxShadow: selectedTab === TAB_KEYS.HEADERS ? `0 0 0 1px ${DefaultTheme.colors.marigold}` : '',
                      }}
                      bg="transparent"
                      p={{ x: '.85rem', y: '.55rem' }}
                      h="0"
                      textSize="caption"
                      rounded="xs"
                      onClick={() => setSelectedTab(TAB_KEYS.HEADERS)}
                    >
                      Headers
                    </Button>
                  </Row>

                  <CodeBlock
                    blockType={selectedTab === TAB_KEYS.BODY ? 'json' : 'text'}
                    theme="dark"
                    divProps={{ minH: '250px' }}
                  >
                    {selectedTab === TAB_KEYS.BODY ? responseBodyText : () => responseHeadersText}
                  </CodeBlock>
                </Row>
              </Div>
            </Col>
          </Row>

          <Row data-debug="layout-row" m={{ x: '0.5rem' }}>
            <Col size={{ xs: '12', sm: '12', md: '12', lg: 'auto', xl: 'auto' }} gutterWidth="0">
              <Div
                rounded="sm"
                p={{ x: '1.25rem', y: '1.25rem' }}
                m={{ y: '1rem' }}
                bg="white"
                d="flex"
                flexDir="column"
              >
                <Row data-debug="return-params" m={{ x: '0', t: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h2" textColor="shade33" textSize="input">
                    Returns
                    {config ? (isActuarialModel ? ' for Model Type: Actuarial' : ' for Model Type: Classifier') : ''}
                  </Text>
                  <Div
                    m={{ x: '0', y: '.5rem' }}
                    p={{ x: '1rem', y: '1rem' }}
                    bg="white"
                    border="1px solid"
                    borderColor="shadeCC"
                    rounded="sm"
                    textColor="shade99"
                    flexGrow="1"
                    minH="120px"
                    overflow="auto"
                    textSize="body"
                  >
                    <Row m={{ l: '-0.5rem', t: '1rem', b: '2rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        <Div tag="span" textWeight="600">
                          api_version
                        </Div>{' '}
                        {`{string}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>
                          As part of Pinpoint&apos;s change management process, updates to the API structure are
                          referenced by versions. Each time changes are released, new versions will be available.
                        </Text>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    <Row m={{ l: '-0.5rem', b: '2rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        <Div tag="span" textWeight="600">
                          cs
                        </Div>{' '}
                        {`{string}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>
                          Each API will be automatically assigned a unique, Configuration Setting Identifier (CS ID),
                          expressed as an 8-character-long alphanumeric string.
                        </Text>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    <Row m={{ l: '-0.5rem', b: '2rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        <Div tag="span" textWeight="600">
                          match_type
                        </Div>{' '}
                        {`{string}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>
                          <Text d="inline" textWeight="600">
                            D&nbsp;
                          </Text>
                          = Deterministic, which means this individual has been uniquely matched among all adults in the
                          United States
                        </Text>
                        <Text m={{ t: '0.75rem' }}>
                          <Text d="inline" textWeight="600">
                            P&nbsp;
                          </Text>
                          = Probabilistic, which means we have identified the correct family household
                        </Text>
                        <Text m={{ t: '0.75rem' }}>
                          <Text d="inline" textWeight="600">
                            N&nbsp;
                          </Text>
                          = Non-Match, as defined as the individual could not be matched to one of the 250+ million US
                          adults in Pinpoint&apos;s repository. Please note that non-matches, as a class, may have worse
                          actual loss and risk outcomes; the average signal from non-matches can be quantified and
                          utilized, if desired.
                        </Text>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    <Row m={{ l: '-0.5rem', b: '2rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        <Div tag="span" textWeight="600">
                          prediction
                        </Div>{' '}
                        {`{object}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>The prediction object that contains the following fields:</Text>
                        <Div tag="ol" style={{ listStyle: 'lower-alpha' }}>
                          {(isActuarialModel || !configId) && (
                            <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                              percentile_premiums (only in the case for Actuarial Loss Predictions and test API)
                            </Div>
                          )}
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            percentile_persons
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            type
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            value
                          </Div>
                        </Div>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    {(isActuarialModel || !configId) && (
                      <>
                        <Row m={{ l: '-0.5rem', b: '2rem' }}>
                          <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                            prediction:{' '}
                            <Div tag="span" textWeight="600">
                              percentile_premiums
                            </Div>{' '}
                            {`{number | null}`}
                          </Col>
                          <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                            <Text>
                              This percentile value indicates the bucket in which a given prediction value falls, within
                              the distribution of prediction values in the model, where buckets (e.g., deciles) contain
                              equal premiums. This property will be sent in the API response of matched records for
                              Actuarial Loss Predictions (Frequency, Severity, Loss Cost Multiplied, and Loss Cost
                              Direct) and the Test API. Return values will be either a number between 0 and 100, with a
                              maximum of 1 decimal place (e.g., 75.3), or a “null” value if the model generation did not
                              calibrate lift buckets by equal premiums”.
                            </Text>
                          </Col>
                        </Row>

                        <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />
                      </>
                    )}

                    <Row m={{ l: '-0.5rem', b: '2rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        prediction:{' '}
                        <Div tag="span" textWeight="600">
                          percentile_persons
                        </Div>{' '}
                        {`{number | null}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>
                          This percentile value indicates the bucket in which a given prediction value falls, within the
                          distribution of prediction values in the model, where buckets (e.g., deciles) contain an equal
                          number of persons. This property will always be sent in the API response of matched records
                          for all APIs. Return values will normally be a number between 0 and 100, with a maximum of 1
                          decimal place (e.g., 75.3). In a few cases, for Actuarial Loss models generated before 2024, a
                          “null” value may be returned if the model generation did not calibrate lift buckets by equal
                          persons.
                        </Text>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    <Row m={{ l: '-0.5rem', b: '2rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        prediction:{' '}
                        <Div tag="span" textWeight="600">
                          type
                        </Div>{' '}
                        {`{string}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>This field describes the prediction type, which will be one of the following:</Text>
                        <Div tag="ol" style={{ listStyle: 'lower-alpha' }}>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            claims_frequency
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            claims_severity
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            loss_cost_direct
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            loss_cost_multiplied
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            binary_classifier - All Risk Scores will show up this as this type
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            test
                          </Div>
                        </Div>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    <Row m={{ l: '-0.5rem', b: '1rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        prediction:{' '}
                        <Div tag="span" textWeight="600">
                          value
                        </Div>{' '}
                        {`{number}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>
                          This field contains the prediction itself. The maximum number of digits the API can return as
                          a prediction value is 17. Depending on the prediction type (see type above), there are
                          different maximum decimal place restrictions, as outlined below:
                        </Text>
                        <Div tag="ol" style={{ listStyle: 'lower-alpha' }}>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            For Loss Predictions
                          </Div>
                          <Div tag="ol" style={{ listStyle: 'lower-roman' }}>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              claims_frequency values have a maximum of 10 decimal places (e.g., 0.0123456789)
                            </Div>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              claims_severity, loss_cost_direct, and loss_cost_multiplied values represent dollar
                              amounts; they therefore have a maximum of 2 decimal places (e.g., 1234.56)
                            </Div>
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            For Risk Scores: values are expressed as numbers between 1.00 and 1000.00.
                          </Div>
                          <Div tag="ol" style={{ listStyle: 'lower-roman' }}>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              These binary classifier prediction values have a maximum of 2 decimal places (e.g.,
                              123.45)
                            </Div>
                          </Div>
                        </Div>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    <Row m={{ l: '-0.5rem', b: '1rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        <Div tag="span" textWeight="600">
                          error
                        </Div>{' '}
                        {`{object}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>
                          This field contains an error message and an error code. In case of error, no other field will
                          be returned by the API. The types of error messages and codes are outlined below:
                        </Text>
                        <Div tag="ol" style={{ listStyle: 'lower-alpha' }}>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            Error messages
                          </Div>
                          <Div tag="ol" style={{ listStyle: 'lower-roman' }}>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              Invalid cs specified, please contact support for a valid customer setting
                            </Div>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              Server error during request
                            </Div>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              OnPoint Service is unavailable; please contact support@pinpoint.ai
                            </Div>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              Model loading; please try again in 20 seconds
                            </Div>
                          </Div>
                          <Div tag="li" m={{ t: '0.5rem', l: '1rem' }}>
                            For error codes
                          </Div>
                          <Div tag="ol" style={{ listStyle: 'lower-roman' }}>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              INV_CS
                            </Div>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              SVC_ERROR
                            </Div>
                            <Div tag="li" m={{ t: '0.5rem', l: '2rem' }}>
                              SVC_INIT
                            </Div>
                          </Div>
                        </Div>
                      </Col>
                    </Row>

                    <Row m={{ b: '2rem' }} border={{ t: '1px solid' }} borderColor="shadeCC" />

                    <Row m={{ l: '-0.5rem', b: '1rem' }}>
                      <Col size={{ xs: '6', sm: '4', md: '6', lg: '4' }}>
                        <Div tag="span" textWeight="600">
                          CloudFront error
                        </Div>{' '}
                        {`{html}`}
                      </Col>
                      <Col size={{ xs: '6', sm: '8', md: '6', lg: '8' }}>
                        <Text>
                          This error is returned by CloudFront, a web service used by Pinpoint to serve static and
                          dynamic web content instead of the normal real-time API responses. This can occur because of
                          resource constraints on the API side. In this scenario, the user may receive the following
                          error texts from CloudFront as html:
                        </Text>
                        <Text>
                          <br />
                          ERROR: The request could not be satisfied <br />
                          503 Service Unavailable ERROR <br />
                          The request could not be satisfied. <br />
                          We can&apos;t connect to the server for this app or website at this time. There might be too
                          much traffic or a configuration error. Try again later, or contact the app or website owner.
                          <br />
                          Generated by cloudfront (CloudFront) HTTP3 Server <br />
                        </Text>
                      </Col>
                    </Row>
                  </Div>
                </Row>
              </Div>
            </Col>

            {/* Spacer column for responsive layout */}
            <Col />

            <Col size={{ xs: '12', sm: '12', md: '12', lg: 'auto', xl: 'auto' }} gutterWidth="0">
              <Div
                rounded="sm"
                p={{ x: '1.25rem', y: '1.25rem' }}
                m={{ y: '1rem' }}
                bg="shade33"
                d="flex"
                flexDir="column"
              >
                <Row data-debug="sample-responses" m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="0" flexWrap="">
                  <Text tag="h2" textColor="white" textSize="input">
                    Sample Responses
                  </Text>
                </Row>

                {(isActuarialModel || !configId) && (
                  <Row m={{ x: '0rem', t: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                    <Text tag="h3" textColor="white">
                      Loss Prediction (match)
                    </Text>

                    <CodeBlock blockType="json" theme="dark" divProps={{ minH: '260px' }}>
                      {sampleLossPredictionText}
                    </CodeBlock>
                  </Row>
                )}

                {!isActuarialModel && (
                  <Row m={{ x: '0', t: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                    <Text tag="h3" textColor="white" textSize="body">
                      Risk Score (match)
                    </Text>

                    <CodeBlock blockType="json" theme="dark" divProps={{ minH: '260px' }}>
                      {sampleRiskScoreText}
                    </CodeBlock>
                  </Row>
                )}

                {!configId && (
                  <Row m={{ x: '0', t: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                    <Text tag="h3" textColor="white" textSize="body">
                      Test Environment (match)
                    </Text>

                    <CodeBlock blockType="json" theme="dark" divProps={{ minH: '260px' }}>
                      {sampleTestText}
                    </CodeBlock>
                  </Row>
                )}

                <Row m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h3" textColor="white" textSize="body">
                    Non-match
                  </Text>

                  <CodeBlock blockType="json" theme="dark" divProps={{ minH: '150px' }}>
                    {sampleNonMatchText}
                  </CodeBlock>
                </Row>
              </Div>

              <Div
                rounded="sm"
                p={{ x: '1.25rem', y: '1.25rem' }}
                m={{ y: '1rem' }}
                bg="shade33"
                d="flex"
                flexDir="column"
              >
                <Row data-debug="sample-responses" m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="0" flexWrap="">
                  <Text tag="h2" textColor="white" textSize="input">
                    Error Responses
                  </Text>
                </Row>

                <Row m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h3" textColor="white" textSize="body">
                    invalid-cs-failure
                  </Text>

                  <CodeBlock blockType="json" theme="dark" divProps={{ minH: '150px' }}>
                    {invalidCSFailure}
                  </CodeBlock>
                </Row>

                <Row m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h3" textColor="white" textSize="body">
                    server-error-during-request
                  </Text>

                  <CodeBlock blockType="json" theme="dark" divProps={{ minH: '150px' }}>
                    {serverErrorDuringRequest}
                  </CodeBlock>
                </Row>

                <Row m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h3" textColor="white" textSize="body">
                    service-unavailable
                  </Text>

                  <CodeBlock blockType="json" theme="dark" divProps={{ minH: '150px' }}>
                    {serviceUnavailable}
                  </CodeBlock>
                </Row>

                <Row m={{ x: '0', y: '.5rem' }} flexDir="column" flexGrow="1" flexWrap="">
                  <Text tag="h3" textColor="white" textSize="body">
                    model-loading
                  </Text>

                  <CodeBlock blockType="json" theme="dark" divProps={{ minH: '150px' }}>
                    {modelLoading}
                  </CodeBlock>
                </Row>
              </Div>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

function CodeBlock({ blockType, minH, theme, divProps, children }) {
  const [wrapLines, setWrapLines] = useState(true);
  const [copied, setCopied] = useState(false);

  const primary = useMemo(() => (theme === 'light' ? 'white' : 'dark'), [theme]);
  const secondary = useMemo(() => (theme === 'light' ? 'shadeF2' : 'shade33'), [theme]);
  const btnColor = useMemo(() => (theme === 'light' ? 'shadeCC' : 'shade66'), [theme]);

  const copyText = useCallback((value) => {
    navigator.clipboard.writeText(value).then(() => setCopied(true));
  }, []);

  const wrapFormat = useCallback((text) => (wrapLines ? JSON.stringify(JSON.parse(text), null, 4) : text), [wrapLines]);

  useEffect(() => {
    if (copied) {
      setTimeout(() => setCopied(false), 2000);
    }
  }, [copied]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Div
      m={{ x: '0', t: '.5rem' }}
      p={{ x: '1rem', y: '1rem' }}
      bg={primary}
      border={theme === 'light' ? '1px solid' : ''}
      borderColor={theme === 'light' ? 'shadeCC' : ''}
      rounded="sm"
      minH={minH}
      d="flex"
      flexDir="column"
      flexGrow="2"
      textColor="shade99"
      {...divProps}
    >
      <Row data-debug="toolbar" justify="space-between" align="flex-start" m={{ x: '-.5rem', t: '-.5rem' }}>
        <Div bg={secondary} p={{ x: '.5rem', y: '.15rem' }} rounded="sm" textSize="tiny">
          {blockType}
        </Div>

        <Div d="flex" m={{ b: '-1rem' }}>
          <Button
            bg={primary}
            hoverBg={secondary}
            w="0"
            h="0"
            p={{ x: '1rem', y: '1rem' }}
            m={{ y: '-.25rem' }}
            onClick={() => setWrapLines(!wrapLines)}
            data-tip="Wrap Lines"
            data-for="custom-wrap-lines"
          >
            <Icon name="WrapLines" color={btnColor} viewBox="0 0 14 12" size=".9rem" />
          </Button>

          <Button
            bg={primary}
            hoverBg={secondary}
            w="0"
            h="0"
            p={{ x: '1rem', y: '1rem' }}
            m={{ l: '.25rem', r: '-.25rem', y: '-.25rem' }}
            onClick={() => copyText(children)}
            key={copied ? 'show-description-copied' : 'show-description-copy'}
            data-tip={copied ? 'Copied' : 'Copy'}
            data-for="custom-copy"
          >
            {copied ? (
              <Icon name="Checked" color="shadeCC" viewBox="0 0 50 50" size=".9rem" />
            ) : (
              <Icon name="Copy" color={btnColor} viewBox="0 0 14 14" size=".9rem" />
            )}
          </Button>
        </Div>
      </Row>

      <Row
        data-debug="response-body"
        flexGrow="1"
        overflow="auto"
        m={{ x: `calc(-${DefaultTheme.grid.gutterWidth}/2)`, t: '1.1rem' }}
      >
        <Text
          tag="pre"
          textSize="body"
          style={{
            fontFamily: 'monospace',
            whiteSpace: 'break-spaces',
            wordBreak: 'break-all',
            lineHeight: '1.5',
          }}
          flexGrow="2"
          m={{ x: '.25rem' }}
          h="0" // height must be set for overflow to work
        >
          {typeof children === 'function' ? children(wrapLines) : wrapFormat(children)}
        </Text>
      </Row>
    </Div>
  );
}

CodeBlock.defaultProps = {
  divProps: {},
};

CodeBlock.propTypes = {
  blockType: PropTypes.string.isRequired,
  minH: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  divProps: PropTypes.shape({}),
};

RtApiDocumentation.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      tenant: PropTypes.string.isRequired,
    }),
  }).isRequired,
  rtApiList: PropTypes.shape({
    apiKey: PropTypes.string.isRequired,
    fetchApiKeyStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED]),
  }).isRequired,
  rtApiDemo: PropTypes.shape({
    config: PropTypes.shape({
      config_metadata: PropTypes.shape({
        data_model_type: PropTypes.string.isRequired,
        model: PropTypes.shape({
          dataModelType: PropTypes.string.isRequired,
        }),
      }).isRequired,
    }),
    fetchConfigStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED]),
  }).isRequired,
  fetchRealTimeApiKey: PropTypes.func.isRequired,
  fetchConfig: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = ({ app, rtApiList, rtApiDemo }, ownProps) => ({
  app,
  rtApiList,
  rtApiDemo,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  fetchRealTimeApiKey: () => dispatch(rtApiListActions.fetchApiKey()),
  fetchConfig: (configId) => dispatch(rtApiDemoActions.fetchConfig(configId)),
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(Page(RtApiDocumentation));
